import { MouseEvent as ReactMouseEvent, ReactNode } from 'react';

import {
  ResizableDivider,
  leftPanelWrapperSx,
  rightPanelWrapperSx,
} from '@backrs_ui/grow-ui/src/components/Lubav/ResizableDivider';

import { Box, SxProps, Theme } from '@mui/material';

import { FAB_HEIGHT } from './MobileLubavFAB';
import { LAYOUT_SCROLL_ID } from './pageShell.layout';

type ResizablePanelLayoutProps = Readonly<{
  showPanel: boolean;
  showMobileFAB?: boolean;
  leftWidth: number;
  isResizing: boolean;
  isCollapsed?: boolean;
  onDividerMouseDown: () => void;
  onExpandMouseDown: (e: ReactMouseEvent) => void;
  leftPanel?: ReactNode;
  children: ReactNode;
}>;

export const RESIZABLE_CONTAINER_ID = 'backrs-content-row';

export const ResizablePanelLayout = ({
  showPanel,
  showMobileFAB = false,
  leftWidth,
  isResizing,
  isCollapsed = false,
  onDividerMouseDown,
  onExpandMouseDown,
  leftPanel,
  children,
}: ResizablePanelLayoutProps) => {
  return (
    <Box id={RESIZABLE_CONTAINER_ID} sx={contentRowSx(isCollapsed)}>
      {showPanel && (
        <Box
          sx={
            [
              leftPanelWrapperSx(leftWidth),
              leftPanelStructureSx,
              !isCollapsed && leftPanelPaddingSx,
              !isResizing && panelTransitionSx,
              isCollapsed && collapsedLeftSx,
            ] as SxProps<Theme>
          }
        >
          {leftPanel}
        </Box>
      )}
      {showPanel && !isCollapsed && <ResizableDivider onMouseDown={onDividerMouseDown} />}
      {showPanel && isCollapsed && (
        <Box
          id="lubav-panel-reopen-handle"
          onMouseDown={onExpandMouseDown}
          sx={collapsedGrabStripSx}
        >
          <Box className="lubav-collapsed-line" sx={collapsedLubavLineSx} />
        </Box>
      )}
      <Box
        sx={
          (showPanel
            ? [
                rightPanelWrapperSx(leftWidth),
                contentAreaSx,
                !isResizing && panelTransitionSx,
                showMobileFAB && fabPaddingSx,
                isCollapsed && collapsedRightSx,
              ]
            : [contentAreaSx, showMobileFAB && fabPaddingSx]) as SxProps<Theme>
        }
        id={LAYOUT_SCROLL_ID}
      >
        {children}
      </Box>
    </Box>
  );
};

const contentRowSx =
  (isCollapsed: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    overflow: 'hidden',
    minHeight: 0,
    paddingLeft: {
      xs: 0,
      md: isCollapsed ? 0 : backrs.spacing['spacing-2xl'],
    },
  });

const leftPanelStructureSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  overflow: 'visible',
  boxSizing: 'border-box',
};

const leftPanelPaddingSx: SxProps<Theme> = ({ backrs }) => ({
  paddingTop: 0,
  paddingBottom: backrs.spacing['spacing-xl'],
});

const collapsedLeftSx: SxProps<Theme> = {
  width: 0,
  minWidth: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
};

const collapsedGrabStripSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  width: '24px',
  display: 'flex',
  alignItems: 'stretch',
  cursor: 'col-resize',
  zIndex: 1,
  // Match the divider: line stops where the chat panel ends.
  pb: backrs.spacing['spacing-xl'],
  '&:hover .lubav-collapsed-line': {
    opacity: 1,
    boxShadow: `0 0 2px ${backrs.colors['Error/200']}, 0 0 4px ${backrs.colors['Error/200']}`,
  },
});

const collapsedLubavLineSx: SxProps<Theme> = ({ backrs }) => ({
  width: '2px',
  height: '100%',
  backgroundColor: backrs.colors['Error/400'],
  // Taper only the right side toward the top; left edge stays flush against the menu.
  borderTopRightRadius: '2px 28px',
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
});

const collapsedRightSx: SxProps<Theme> = {
  flex: 1,
  width: 'auto',
  minWidth: 0,
};

const contentAreaSx: SxProps<Theme> = {
  flex: 1,
  overflowY: 'auto',
  height: '100%',
  minWidth: 0,
};

const fabPaddingSx: SxProps<Theme> = {
  paddingBottom: FAB_HEIGHT,
};

const panelTransitionSx: SxProps<Theme> = { transition: 'width 0.3s ease-in-out' };
